import { create } from "zustand";
import { devtools, persist } from "zustand/middleware";

import { getIsProd } from "../config";

export type RapportLocation = {
    state?: string | null;
    city?: string | null;
    timezone?: string;
};

type RapportBuildingStore = {
    questions: string[][];
    location?: RapportLocation;
    hasQuestions: () => boolean;
    hasLocation: () => boolean;
    updateQuestions: (questions: string[][]) => void;
    updateLocation: (location: RapportLocation) => void;
    reset: () => void;
};

const INITIAL_STATE = {
    questions: [] as string[][],
    location: undefined as RapportLocation | undefined,
};

export const useRapportBuildingStore = create<RapportBuildingStore>()(
    persist(
        devtools(
            (set, get) => ({
                ...INITIAL_STATE,
                hasQuestions: () => get().questions.length !== 0,
                hasLocation: (): boolean => {
                    const { location } = get();
                    if (location == null) return false;
                    const { city, state, timezone } = location;
                    return !!(city || state || timezone);
                },
                updateQuestions: (questions: string[][]) => set({ questions }),
                updateLocation: (location: RapportLocation) => set({ location }),
                reset: () => set(INITIAL_STATE),
            }),
            {
                enabled: !getIsProd(),
                store: "rapport-questions",
                name: "workspace/sales-tools",
            },
        ),
        { name: "rapport-questions" },
    ),
);

/**
 * Handle session end — resets rapport building store.
 *
 * In expert-ui, this is triggered by sdkEventBus.on("session_changed", ...).
 * The host should call this when a session ends:
 *
 *   import { handleSessionEnd } from "@expert/sales-tools";
 *   sdkEventBus.on("session_changed", ({ action }) => {
 *       if (action === "end") handleSessionEnd();
 *   });
 */
export function handleSessionEnd() {
    useRapportBuildingStore.getState().reset();
}
